import { useMutation } from "react-query";

import axios, { AxiosResponse } from "axios";
import { withAwsPresetRange } from "entities/charts/eva/requests/wellStatus";
import { WellStatusChartRequestType } from "types";
import { ExportFormat } from "utils/exportUtils";
import * as z from "zod";

const chartServiceApiRoot = process.env.REACT_APP_CHART_SERVICE;

export const ExportWellStatusResponseSchema = z.object({
  data: z.instanceof(Blob),
  status: z.number().min(200).max(299)
});

export type ExportWellStatusResponse = z.infer<typeof ExportWellStatusResponseSchema>;

export interface ExportWellStatusParams {
  request: WellStatusChartRequestType;
  format: ExportFormat;
  filename: string;
}

/**
 * Export mutation for the Well Status charts. Mirrors
 * useExportRigFracActivityMutation: POSTs the LEAN well-status request to the
 * dedicated wellStatusChart export route (NOT the wells /chart/export), and both
 * chart types share it — the route reads `chartType` to pick the table shape.
 */
export default function useExportWellStatusMutation(
  onSuccess?: (response: AxiosResponse, params: ExportWellStatusParams) => void,
  onError?: (error?: Error) => void
) {
  return useMutation(
    async ({ request, format }: ExportWellStatusParams) => {
      const response = await axios.post(
        // chartServiceApiRoot already ends in /api/v1.
        `${chartServiceApiRoot}/wellStatusChart/export?exportformat=${format}`,
        withAwsPresetRange(request),
        {
          responseType: "blob"
        }
      );

      // A no-data export answers HTTP 200 with a JSON ChartResult body (the
      // widget-state convention), which responseType:"blob" still wraps in a
      // Blob — so a Blob+2xx check alone would save the JSON as a spreadsheet.
      // Only a csv/xlsx content-type is a real file.
      const contentType = String(response.headers?.["content-type"] ?? "");
      if (contentType.includes("application/json")) {
        throw new Error("No data to export");
      }

      const validatedResponse = ExportWellStatusResponseSchema.parse(response);
      return validatedResponse as AxiosResponse<Blob>;
    },
    {
      onSuccess: onSuccess
        ? (response, params) => onSuccess(response, params)
        : undefined,
      onError: onError ? (error) => onError(error as Error) : undefined
    }
  );
}
